
import React, { useState, useEffect } from 'react';
import Introduction from './components/Introduction';
import MakhrajView from './components/MakhrajView';
import SifatView from './components/SifatView';
import RulesView from './components/RulesView';
import { AppData } from './types';
import { fetchTajwidData } from './services/googleSheetsService';

const TAJWID_TIPS = [
  "Mempelajari Tajwid adalah Fardhu Kifayah.",
  "Membaca Al-Quran dengan Tajwid adalah Fardhu 'Ain.",
  "Huruf Ikhfa Haqiqi berjumlah 15 huruf.",
  "Ghunnah dibaca mendengung selama 2 harakat.",
  "Makhraj Al-Halq berarti huruf yang keluar dari tenggorokan.",
  "Idgham bi Ghunnah berarti melebur dengan dengung."
];

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'intro' | 'makhraj' | 'sifat' | 'rules'>('intro');
  const [data, setData] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const result = await fetchTajwidData();
        setData(result);
      } catch (err) {
        console.error(err);
        setError("Gagal memuat data aplikasi.");
      } finally {
        setTimeout(() => setLoading(false), 800);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setTipIndex((prev) => (prev + 1) % TAJWID_TIPS.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-bg overflow-hidden relative">
        <div className="absolute inset-0 bg-primary opacity-5"></div>
        <div className="relative z-10 flex flex-col items-center p-6">
          <div className="relative w-24 h-24 md:w-32 md:h-32 mb-8">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                 <i className="fa-solid fa-book-quran text-primary text-2xl md:text-3xl"></i>
              </div>
            </div>
          </div>
          <h2 className="text-dark text-lg font-black tracking-widest uppercase mb-4">MEMUAT DATA...</h2>
          <div className="max-w-xs text-center">
            <p className="text-body font-medium italic text-sm md:text-base animate-fadeIn h-12">
              "{TAJWID_TIPS[tipIndex]}"
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-dark pb-28 md:pb-32">
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-stroke shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-16 md:h-20 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2.5 rounded-xl text-primary">
               <i className="fa-solid fa-kaaba text-xl md:text-2xl"></i>
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-black tracking-tight text-dark">POST CARD TAJWID</h1>
              <p className="text-[10px] md:text-[11px] text-body font-bold uppercase tracking-widest">E-Learning Dashboard</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-danger/10 border-l-4 border-danger p-4 mb-6 rounded-lg animate-fadeIn">
            <p className="text-danger font-bold text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl md:rounded-3xl shadow-sm border border-stroke min-h-[60vh] overflow-hidden">
          {data ? (
            <>
              {activeTab === 'intro' && data.introduction && <Introduction data={data.introduction} />}
              {activeTab === 'makhraj' && data.makhraj && <MakhrajView makhraj={data.makhraj} />}
              {activeTab === 'sifat' && data.sifat && <SifatView sifat={data.sifat} />}
              {activeTab === 'rules' && data.rules && <RulesView rules={data.rules} />}
            </>
          ) : (
            <div className="p-20 text-center text-body font-medium">Data gagal dimuat.</div>
          )}
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-50 flex justify-center p-4 pointer-events-none">
        <div className="glass-strong rounded-full flex justify-around w-full max-w-lg px-2 py-2 pointer-events-auto shadow-2xl border border-white/50">
          <NavButton active={activeTab === 'intro'} onClick={() => setActiveTab('intro')} label="Home" icon="fa-house" />
          <NavButton active={activeTab === 'makhraj'} onClick={() => setActiveTab('makhraj')} label="Makhraj" icon="fa-lips" />
          <NavButton active={activeTab === 'sifat'} onClick={() => setActiveTab('sifat')} label="Sifat" icon="fa-wand-magic-sparkles" />
          <NavButton active={activeTab === 'rules'} onClick={() => setActiveTab('rules')} label="Hukum" icon="fa-book-open" />
        </div>
      </nav>
    </div>
  );
};

interface NavButtonProps {
  active: boolean;
  label: string;
  icon: string;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ active, label, icon, onClick }) => (
  <button 
    onClick={onClick}
    className={`flex-1 flex flex-col items-center py-2 transition-all relative group rounded-full ${active ? 'text-primary' : 'text-body hover:text-primary'}`}
  >
    <div className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${active ? 'bg-primary text-white shadow-lg shadow-primary/30 translate-y-[-8px]' : 'bg-transparent group-hover:bg-bg'}`}>
      <i className={`fa-solid ${icon} ${active ? 'text-sm' : 'text-lg'}`}></i>
    </div>
    <span className={`text-[10px] font-bold mt-1 transition-all ${active ? 'opacity-100 font-black' : 'opacity-0 h-0 overflow-hidden group-hover:opacity-100 group-hover:h-auto'}`}>{label}</span>
  </button>
);

export default App;
